import * as DialogPrimitive from '@radix-ui/react-dialog'
import type { DialogProps } from '@radix-ui/react-dialog'
import type { PropsWithChildren } from 'react'
import { useEffect, useRef } from 'react'
import styled, { keyframes } from 'styled-components'

import { CloseIcon } from 'Icons'

const overlayShow = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`

const contentShow = keyframes`
  0% {
    opacity: 0;
    transform: translate(-50%, -48%) scale(.96);
  }
  100% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`

const OverlayStyled = styled(DialogPrimitive.Overlay)`
  background-color: rgba(0, 0, 0, 0.45);
  position: fixed;
  inset: 0;
  z-index: 2;

  @media (prefers-reduced-motion: no-preference) {
    animation: ${overlayShow} 150ms cubic-bezier(0.16, 1, 0.3, 1);
  }
`

const ContentStyled = styled(DialogPrimitive.Content)`
  background: #ffff;
  box-shadow: 3px 4px 10px rgba(0, 0, 0, 0.25);
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 16px;
  border-radius: 10px;
  z-index: 2;

  @media (prefers-reduced-motion: no-preference) {
    animation: ${contentShow} 150ms cubic-bezier(0.16, 1, 0.3, 1);
  }
  &:focus {
    outline: none;
  }
`

const CloseButtonWrap = styled.button`
  border: none;
  outline: none;
  width: 2.25rem;
  height: 2.25rem;
  position: absolute;
  top: 12px;
  right: 12px;
  display: grid;
  place-items: center;
  cursor: pointer;
  background: #ffffff;
  z-index: 999;

  svg {
    width: 18px;
    height: 18px;
  }
`

const Dialog = DialogPrimitive.Root
const DialogTrigger = DialogPrimitive.Trigger

const DialogClose = DialogPrimitive.Close

function DialogContent({
  ...props
}: PropsWithChildren<
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>) {
  return (
    <DialogPrimitive.Portal>
      <OverlayStyled />
      <ContentStyled {...props} />
    </DialogPrimitive.Portal>
  )
}
export interface ModalProps extends DialogProps {
  trigger?: React.ReactNode // trigger element
  className?: string // for modal body
  children: React.ReactNode
}

function Modal({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  defaultOpen,
  trigger,
  className,
  children,
  ...props
}: PropsWithChildren<ModalProps>) {
  const closeBtnRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && closeBtnRef) {
        closeBtnRef.current?.click()
      }
    }
    window.addEventListener('keydown', handleEsc)
    return () => {
      window.removeEventListener('keydown', handleEsc)
    }
  }, [])

  return (
    <Dialog {...props}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className={className}>
        <DialogClose asChild>
          <CloseButtonWrap>
            <CloseIcon />
          </CloseButtonWrap>
        </DialogClose>
        {children}
      </DialogContent>
    </Dialog>
  )
}

export default Modal
